import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTheme } from './ThemeContext';
import { useLanguage } from './LanguageContext';
import { useAuth } from './AuthContext';
import api from '../utils/axiosConfig';

const SettingsContext = createContext();

export const useSettings = () => useContext(SettingsContext);

const DEFAULT_HEALTH_SYNC_CONSENT = {
  enabled: false,
  sleep: false,
  vitals: false,
  consentVersion: 1,
};

export const SettingsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { setTheme: setThemeContext, mode, scheme } = useTheme();
  const { setLanguage: setLanguageContext } = useLanguage();

  const [settings, setSettingsState] = useState({
    theme: 'light-blue',
    language: 'en',
    maxHeartrate: null,
    weightKg: null,
    runningCriticalPowerWatts: null,
    distanceUnits: 'km',
    widgetLibraryEditExpanded: true,
    zitiusAiUserContentCollapsed: false,
    zitiusHealthSyncConsent: DEFAULT_HEALTH_SYNC_CONSENT,
  });
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await api.get('/api/users/settings');
      const data = response.data;
      setSettingsState({
        theme: data.theme || `${mode}-${scheme}`,
        language: data.language || 'en',
        maxHeartrate: data.maxHeartrate ?? null,
        weightKg: data.weightKg ?? null,
        runningCriticalPowerWatts: data.runningCriticalPowerWatts ?? null,
        distanceUnits: data.distanceUnits === 'miles' ? 'miles' : 'km',
        widgetLibraryEditExpanded: data.widgetLibraryEditExpanded !== false,
        zitiusAiUserContentCollapsed: data.zitiusAiUserContentCollapsed === true,
        zitiusHealthSyncConsent: {
          ...DEFAULT_HEALTH_SYNC_CONSENT,
          ...(data.zitiusHealthSyncConsent && typeof data.zitiusHealthSyncConsent === 'object'
            ? data.zitiusHealthSyncConsent
            : {}),
        },
      });
    } catch {
      setSettingsState((prev) => prev);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, mode, scheme]);

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, fetchSettings]);

  const updateSetting = useCallback(
    async (key, value) => {
      const payload = {};
      if (key === 'theme') {
        payload.theme = value;
        const [m, s] = (value || 'light-blue').split('-');
        setThemeContext(m || 'light', s || 'blue');
      } else if (key === 'language') {
        payload.language = value;
        setLanguageContext(value || 'en');
      } else if (key === 'maxHeartrate' || key === 'max_heartrate') {
        if (value === null || value === '') {
          payload.maxHeartrate = null;
        } else {
          const parsed = parseInt(value, 10);
          if (!Number.isNaN(parsed) && parsed >= 40 && parsed <= 250) {
            payload.maxHeartrate = parsed;
          }
        }
      } else if (key === 'weightKg' || key === 'weight_kg') {
        if (value === null || value === '') {
          payload.weightKg = null;
        } else {
          const parsed = parseFloat(value);
          if (!Number.isNaN(parsed) && parsed >= 30 && parsed <= 200) {
            payload.weightKg = parsed;
          }
        }
      } else if (key === 'runningCriticalPowerWatts' || key === 'running_critical_power_watts') {
        if (value === null || value === '') {
          payload.runningCriticalPowerWatts = null;
        } else {
          const parsed = parseFloat(value);
          if (!Number.isNaN(parsed) && parsed >= 50 && parsed <= 800) {
            payload.runningCriticalPowerWatts = Math.round(parsed * 10) / 10;
          }
        }
      } else if (key === 'widgetLibraryEditExpanded') {
        payload.widgetLibraryEditExpanded = value === true;
      } else if (key === 'zitiusAiUserContentCollapsed') {
        payload.zitiusAiUserContentCollapsed = value === true;
      } else if (key === 'distanceUnits' || key === 'distance_units') {
        payload.distanceUnits = String(value).toLowerCase() === 'miles' ? 'miles' : 'km';
      } else if (key === 'zitiusHealthSyncConsent') {
        if (value && typeof value === 'object') {
          payload.zitiusHealthSyncConsent = value;
        }
      }

      if (Object.keys(payload).length === 0) return;

      try {
        await api.put('/api/users/settings', payload);
        if (payload.maxHeartrate !== undefined || payload.weightKg !== undefined) {
          window.dispatchEvent(new Event('thresholdsSettingsChanged'));
        }
        setSettingsState((prev) => ({
          ...prev,
          theme: payload.theme ?? prev.theme,
          language: payload.language ?? prev.language,
          maxHeartrate: payload.maxHeartrate !== undefined ? payload.maxHeartrate : prev.maxHeartrate,
          weightKg: payload.weightKg !== undefined ? payload.weightKg : prev.weightKg,
          runningCriticalPowerWatts:
            payload.runningCriticalPowerWatts !== undefined
              ? payload.runningCriticalPowerWatts
              : prev.runningCriticalPowerWatts,
          distanceUnits: payload.distanceUnits !== undefined ? payload.distanceUnits : prev.distanceUnits,
          widgetLibraryEditExpanded:
            payload.widgetLibraryEditExpanded !== undefined
              ? payload.widgetLibraryEditExpanded
              : prev.widgetLibraryEditExpanded,
          zitiusAiUserContentCollapsed:
            payload.zitiusAiUserContentCollapsed !== undefined
              ? payload.zitiusAiUserContentCollapsed
              : prev.zitiusAiUserContentCollapsed,
          zitiusHealthSyncConsent:
            payload.zitiusHealthSyncConsent !== undefined
              ? { ...prev.zitiusHealthSyncConsent, ...payload.zitiusHealthSyncConsent }
              : prev.zitiusHealthSyncConsent,
        }));
      } catch (err) {
        console.error('Failed to update setting:', err);
      }
    },
    [setThemeContext, setLanguageContext]
  );

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        theme: settings.theme,
        language: settings.language,
        maxHeartrate: settings.maxHeartrate,
        weightKg: settings.weightKg,
        runningCriticalPowerWatts: settings.runningCriticalPowerWatts,
        distanceUnits: settings.distanceUnits,
        widgetLibraryEditExpanded: settings.widgetLibraryEditExpanded,
        zitiusAiUserContentCollapsed: settings.zitiusAiUserContentCollapsed,
        zitiusHealthSyncConsent: settings.zitiusHealthSyncConsent,
        updateSetting,
        fetchSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
